import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { 
  Box, 
  Typography, 
  TextField, 
  Button, 
  CircularProgress, 
  Card, 
  CardContent,
  Alert,
  Divider,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  Avatar,
  Grid,
  Container,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Stack,
  Chip,
  IconButton,
  Tooltip
} from '@mui/material';
import axios from 'axios';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import DescriptionIcon from '@mui/icons-material/Description';
import AnimatedLogo from '../components/common/AnimatedLogo';
import { useAuth } from '../context/AuthContext';
import RefreshIcon from '@mui/icons-material/Refresh';
import PersonIcon from '@mui/icons-material/Person';
import { useNavigate } from 'react-router-dom';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { useSnackbar } from 'notistack';

const AboutSection = () => {
  const { user } = useAuth();
  const [profileUrl, setProfileUrl] = useState('');
  const [profileData, setProfileData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [aboutAnalysis, setAboutAnalysis] = useState(null);
  const [analyzingAbout, setAnalyzingAbout] = useState(false);
  const [userProfile, setUserProfile] = useState(null);
  const [initialLoading, setInitialLoading] = useState(true);
  const [isFromCache, setIsFromCache] = useState(false);
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [generateDialogOpen, setGenerateDialogOpen] = useState(false);
  const [generatingAbout, setGeneratingAbout] = useState(false);
  const [generatedAboutText, setGeneratedAboutText] = useState(null);
  const [generationForm, setGenerationForm] = useState({
    role: '',
    experience: '',
    skills: '',
    achievements: '',
    industry: '',
    targetAudience: '',
    style: 'professional'
  });

  useEffect(() => {
    const fetchUserProfile = async () => {
      setInitialLoading(true);
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/auth/profile`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('token')}`
            }
          }
        );
        
        const userData = response.data.user;
        setUserProfile(userData);

        if (userData.linkedinProfile) {
          // Try to get existing analysis from database
          try {
            const analysisResponse = await axios.get(
              `${process.env.REACT_APP_API_URL}/api/linkedin/about-analysis`,
              {
                headers: {
                  Authorization: `Bearer ${localStorage.getItem('token')}`
                }
              }
            );
            
            if (analysisResponse.data.success) {
              setProfileData({ about: analysisResponse.data.analysis.about_text });
              setAboutAnalysis({
                score: analysisResponse.data.analysis.analysis_score,
                analysis: analysisResponse.data.analysis.analysis_text,
                category_scores: analysisResponse.data.analysis.categoryScores,
                strengths: analysisResponse.data.analysis.strengths,
                recommendations: analysisResponse.data.analysis.recommendations
              });
            } else {
              setProfileUrl(userData.linkedinProfile);
              await handleFetchProfile(userData.linkedinProfile, false);
            }
          } catch (err) {
            if (err.response?.status === 404) {
              // No analysis found in database, fetch fresh data
              setProfileUrl(userData.linkedinProfile);
              await handleFetchProfile(userData.linkedinProfile, false);
            } else {
              console.error('Error fetching analysis:', err);
            }
          }
        }
      } catch (err) {
        console.error('Error fetching user profile:', err);
      } finally {
        setInitialLoading(false);
      }
    };

    fetchUserProfile();
  }, []);

  const handleFetchProfile = async (url = null, showLoading = true) => {
    const urlToUse = url || profileUrl;
    
    if (!urlToUse) {
      setError('Please enter a LinkedIn profile URL');
      return;
    }

    if (showLoading) {
      setLoading(true);
    }
    setError('');
    setProfileData(null);
    setAboutAnalysis(null);
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/linkedin/profile-data`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      console.log('LinkedIn profile data:', response.data);
      setProfileData(response.data);
      
      if (response.data.about) {
        await analyzeAboutSection(response.data.about);
      }
    } catch (err) {
      console.error('Error fetching LinkedIn profile:', err);
      setError(err.response?.data?.message || 'Failed to fetch LinkedIn profile data');
    } finally {
      if (showLoading) {
        setLoading(false);
      }
    }
  };

  const analyzeAboutSection = async (aboutText) => {
    try {
      setAnalyzingAbout(true);
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/linkedin/analyze-about`,
        { aboutText },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      if (response.data && (response.data.analysis || response.data.score)) {
        const analysisData = response.data;
        setAboutAnalysis(analysisData);
        
        // Calculate total score from category scores
        const totalScore = analysisData.category_scores 
          ? Object.values(analysisData.category_scores).reduce((sum, score) => sum + score, 0)
          : analysisData.score;
        
        // Ensure all values are defined before sending to backend
        const analysisPayload = {
          aboutText: aboutText || '',
          analysisScore: totalScore || 0,
          analysisText: analysisData.analysis || '',
          categoryScores: analysisData.category_scores || {},
          strengths: analysisData.strengths || [],
          recommendations: analysisData.recommendations || []
        };

        // Save analysis to database
        await axios.post(
          `${process.env.REACT_APP_API_URL}/api/linkedin/about-analysis`,
          analysisPayload,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );
      } else {
        console.error('Invalid analysis response:', response.data);
        setError('Failed to analyze about section. Invalid response format.');
      }
    } catch (err) {
      console.error('Error analyzing about section:', err);
      setError(err.response?.data?.message || 'Failed to analyze about section');
    } finally {
      setAnalyzingAbout(false);
    }
  };

  const getScoreColor = (percentage) => {
    if (percentage >= 80) return '#48BB78';
    if (percentage >= 60) return '#4299E1';
    if (percentage >= 40) return '#ECC94B';
    return '#F56565';
  };

  const handleRefreshData = () => {
    localStorage.removeItem('linkedinAboutData');
    localStorage.removeItem('linkedinAboutAnalysis');
    localStorage.removeItem('linkedinAboutTimestamp');
    
    handleFetchProfile(userProfile?.linkedinProfile);
  };

  const handleGenerateAbout = async () => {
    try {
      setGeneratingAbout(true);
      setGeneratedAboutText(null);
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/linkedin/generate-about`,
        {
          role: generationForm.role,
          experience: generationForm.experience,
          skills: generationForm.skills,
          achievements: generationForm.achievements,
          industry: generationForm.industry,
          targetAudience: generationForm.targetAudience,
          style: generationForm.style
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        }
      );
      
      if (response.data && response.data.about_text) {
        setGeneratedAboutText(response.data.about_text);
        
        setGenerateDialogOpen(false);
        
        enqueueSnackbar('About section generated successfully!', { variant: 'success' });
      } else {
        throw new Error('Invalid response format from generation service');
      }
    } catch (err) {
      console.error('Error generating about section:', err);
      enqueueSnackbar(
        err.response?.data?.message || 'Failed to generate about section. Please try again.',
        { variant: 'error' }
      );
    } finally {
      setGeneratingAbout(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>LinkedIn About Section Analyzer | Instation</title>
        <meta name="description" content="Analyze and improve your LinkedIn About section with Instation's AI-powered analyzer tool." />
      </Helmet>

      <Container maxWidth="xl" className="page-container" sx={{ overflow: 'visible' }}>
        <Box className="content-container" sx={{ overflow: 'visible', height: 'auto' }}>
          {initialLoading ? (
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center', 
              justifyContent: 'center',
              height: 'calc(100vh - 200px)',
              width: '100%'
            }}>
              <AnimatedLogo size={80} />
              <Typography variant="h6" sx={{ mt: 3 }}>
                Loading profile data...
              </Typography>
            </Box>
          ) : (
            <Grid container spacing={3}>
              <Grid item xs={12} className="content-panel" sx={{ overflow: 'visible', height: 'auto' }}>
                <Paper elevation={0} className="content-paper" sx={{ p: 3, borderRadius: 2, border: '1px solid rgba(0, 0, 0, 0.08)', overflow: 'visible' }}>
                  <Typography variant="h6" className="component-heading" sx={{ mb: 2 }}>
                    LinkedIn About Section Analyzer
                  </Typography>
                  
                  {!userProfile?.linkedinProfile ? (
                    <Box 
                      sx={{ 
                        display: 'flex', 
                        flexDirection: 'column', 
                        alignItems: 'center', 
                        justifyContent: 'center',
                        py: 8,
                        height: 'calc(100vh - 400px)',
                        width: '100%'
                      }}
                    >
                      <AnimatedLogo size={80} />
                      <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                        No LinkedIn Profile Connected
                      </Typography>
                      <Typography variant="body1" color="text.secondary" sx={{ mb: 3, maxWidth: 500 }}>
                        To analyze your LinkedIn About section, please add your LinkedIn profile URL in your profile settings.
                      </Typography>
                      <Button
                        variant="contained"
                        startIcon={<PersonIcon />}
                        sx={{
                          borderRadius: 2,
                          textTransform: 'none',
                          fontWeight: 600,
                          bgcolor: 'var(--primary-color)',
                          '&:hover': {
                            bgcolor: 'var(--primary-dark)'
                          }
                        }}
                        onClick={() => navigate('/dashboard/profile')}
                      >
                        Go to Profile Settings
                      </Button>
                    </Box>
                  ) : (
                    <Box 
                      sx={{ 
                        mb: 4,
                        p: 2,
                        borderRadius: 2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        bgcolor: '#ffffff',
                        border: '1px solid rgba(0, 0, 0, 0.08)',
                        boxShadow: '0 2px 10px rgba(0, 0, 0, 0.05)'
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Avatar
                          src={profileData?.profile_picture || userProfile?.image}
                          sx={{ 
                            width: 48, 
                            height: 48, 
                            mr: 2,
                            border: '2px solid #0A66C2'
                          }}
                        >
                          {(profileData?.name || userProfile?.name) ? (profileData?.name || userProfile?.name).charAt(0) : <PersonIcon />}
                        </Avatar>
                        
                        <Box>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#2D3748' }}>
                            {profileData?.name || userProfile?.name || 'Your Profile'}
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <LinkedInIcon 
                              sx={{ 
                                color: '#0A66C2', 
                                fontSize: 16, 
                                mr: 0.5 
                              }} 
                            />
                            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                              {userProfile.linkedinProfile}
                            </Typography>
                            {isFromCache && (
                              <Typography 
                                variant="caption" 
                                sx={{ 
                                  ml: 1, 
                                  px: 1, 
                                  py: 0.2, 
                                  bgcolor: 'rgba(66, 153, 225, 0.1)', 
                                  borderRadius: 1,
                                  color: '#4299E1',
                                  fontWeight: 500
                                }}
                              >
                                Cached
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      </Box>
                      
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                          variant="contained"
                          onClick={() => setGenerateDialogOpen(true)}
                          startIcon={<AutoAwesomeIcon />}
                          size="small"
                          sx={{
                            borderRadius: 2,
                            textTransform: 'none',
                            fontWeight: 600,
                            bgcolor: '#9c27b0',
                            '&:hover': {
                              bgcolor: '#7b1fa2'
                            }
                          }}
                        >
                          Generate About
                        </Button>
                        
                        <Button
                          variant="contained"
                          onClick={handleRefreshData}
                          disabled={loading}
                          startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon />}
                          size="small"
                          sx={{
                            borderRadius: 2,
                            textTransform: 'none',
                            fontWeight: 600,
                            bgcolor: 'var(--primary-color)',
                            px: 2,
                            '&:hover': {
                              bgcolor: 'var(--primary-dark)'
                            }
                          }}
                        >
                          {loading ? 'Analyzing...' : isFromCache ? 'Refresh Data' : 'Refresh Analysis'}
                        </Button>
                      </Box>
                    </Box>
                  )}
                  
                  {error && (
                    <Alert severity="error" sx={{ mb: 3 }}>
                      {error}
                    </Alert>
                  )}
                  
                  {loading && (
                    <Box sx={{ 
                      display: 'flex', 
                      flexDirection: 'column', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                      py: 8
                    }}>
                      <AnimatedLogo size={80} />
                      <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>
                        Analyzing LinkedIn Profile
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        This may take a moment...
                      </Typography>
                    </Box>
                  )}
                  
                  {profileData && !loading && (
                    <Box>
                      <Typography variant="h6" className="section-title" sx={{ mb: 2, fontWeight: 500, color: 'black' }}>
                        About Section Analysis for {profileData.name}
                      </Typography>
                      
                      <Card className="content-paper" sx={{ mb: 4, borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.05)', border: '1px solid rgba(0, 0, 0, 0.08)' }}>
                        <Typography variant="subtitle1" className="section-title" sx={{ p: 2, bgcolor: 'rgba(0,0,0,0.03)', fontWeight: 500, color: 'black' }}>
                          About Section
                        </Typography>
                        
                        <CardContent>
                          {profileData.about ? (
                            <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>
                              {profileData.about}
                            </Typography>
                          ) : (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                              <DescriptionIcon color="action" />
                              <Typography variant="body2" color="text.secondary">
                                No about section found. Adding a compelling about section can significantly improve your LinkedIn profile.
                              </Typography>
                            </Box>
                          )}
                        </CardContent>
                      </Card>
                      
                      <Card className="content-paper" sx={{ borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.05)', border: '1px solid rgba(0, 0, 0, 0.08)' }}>
                        <Typography variant="subtitle1" className="section-title" sx={{ p: 2, bgcolor: 'rgba(0,0,0,0.03)', fontWeight: 500, color: 'black' }}>
                          About Section Analysis
                        </Typography>
                        
                        <CardContent>
                          {analyzingAbout && (
                            <Box sx={{ mb: 2 }}>
                              <Typography variant="body2" color="text.secondary" gutterBottom>
                                Analyzing about section...
                              </Typography>
                              <LinearProgress sx={{ 
                                '& .MuiLinearProgress-bar': { 
                                  bgcolor: 'var(--primary-color)' 
                                } 
                              }} />
                            </Box>
                          )}
                          
                          {aboutAnalysis && (
                            <Box>
                              <Box sx={{ mb: 3, display: 'flex', alignItems: 'center' }}>
                                <Typography variant="h6" fontWeight={600}>
                                  About Section Score: {
                                    aboutAnalysis.category_scores 
                                      ? Object.values(aboutAnalysis.category_scores).reduce((sum, score) => sum + score, 0)
                                      : aboutAnalysis.score
                                  }/{aboutAnalysis.category_scores ? Object.keys(aboutAnalysis.category_scores).length * 10 : 70}
                                </Typography>
                                <Box 
                                  sx={{ 
                                    ml: 2, 
                                    px: 2, 
                                    py: 0.5, 
                                    borderRadius: 10, 
                                    bgcolor: `${getScoreColor(aboutAnalysis.score)}20`,
                                    color: getScoreColor(aboutAnalysis.score),
                                    fontWeight: 600
                                  }}
                                >
                                  {aboutAnalysis.score}%
                                </Box>
                              </Box>
                              
                              {aboutAnalysis.category_scores && (
                                <Box sx={{ mb: 3 }}>
                                  <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                                    Score Breakdown:
                                  </Typography>
                                  <Grid container spacing={2}>
                                    {Object.entries(aboutAnalysis.category_scores).map(([category, score]) => (
                                      <Grid item xs={12} sm={6} key={category}>
                                        <Box sx={{ mb: 1 }}>
                                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                            <Typography variant="body1" color="text.primary">
                                              {category.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
                                            </Typography>
                                            <Typography variant="body1" fontWeight={500}>
                                              {score}/10
                                            </Typography>
                                          </Box>
                                          <LinearProgress 
                                            variant="determinate" 
                                            value={score * 10} 
                                            sx={{ 
                                              height: 6, 
                                              borderRadius: 3,
                                              bgcolor: 'rgba(0,0,0,0.05)',
                                              '& .MuiLinearProgress-bar': {
                                                bgcolor: getScoreColor(score * 10)
                                              }
                                            }} 
                                          />
                                        </Box>
                                      </Grid>
                                    ))}
                                  </Grid>
                                </Box>
                              )}
                              
                              <Box sx={{ mb: 3 }}>
                                <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                                  Analysis:
                                </Typography>
                                <Typography variant="body1" color="text.primary" paragraph>
                                  {aboutAnalysis.analysis}
                                </Typography>
                              </Box>
                              
                              {aboutAnalysis.strengths && aboutAnalysis.strengths.length > 0 && (
                                <Box sx={{ mb: 3 }}>
                                  <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                                    Strengths:
                                  </Typography>
                                  <List dense disablePadding>
                                    {aboutAnalysis.strengths.map((strength, index) => (
                                      <ListItem key={index} sx={{ py: 0.5 }}>
                                        <ListItemText 
                                          primary={strength}
                                          primaryTypographyProps={{
                                            variant: 'body1',
                                            color: 'text.primary'
                                          }}
                                        />
                                      </ListItem>
                                    ))}
                                  </List>
                                </Box>
                              )}
                              
                              {aboutAnalysis.recommendations && aboutAnalysis.recommendations.length > 0 && (
                                <Box>
                                  <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                                    Recommendations:
                                  </Typography>
                                  <List dense disablePadding>
                                    {aboutAnalysis.recommendations.map((rec, index) => (
                                      <ListItem key={index} sx={{ py: 0.5 }}>
                                        <ListItemText 
                                          primary={rec}
                                          primaryTypographyProps={{
                                            variant: 'body1',
                                            color: 'text.primary'
                                          }}
                                        />
                                      </ListItem>
                                    ))}
                                  </List>
                                </Box>
                              )}
                            </Box>
                          )}
                          
                          {!aboutAnalysis && !analyzingAbout && (
                            <Typography variant="body2" color="text.secondary">
                              Your about section is a critical part of your LinkedIn profile. It's often the first thing recruiters and connections read to learn more about you.
                            </Typography>
                          )}
                        </CardContent>
                      </Card>
                    </Box>
                  )}
                </Paper>
              </Grid>
            </Grid>
          )}
        </Box>

        <Dialog 
          open={generateDialogOpen} 
          onClose={() => !generatingAbout && setGenerateDialogOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle sx={{ pb: 1 }}>
            Generate About Section
          </DialogTitle>
          
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <TextField
                label="Professional Role"
                value={generationForm.role}
                onChange={(e) => setGenerationForm(prev => ({ ...prev, role: e.target.value }))}
                fullWidth
                required
                placeholder="e.g., Full-Stack Developer, UX Designer"
              />
              
              <TextField
                label="Experience"
                value={generationForm.experience}
                onChange={(e) => setGenerationForm(prev => ({ ...prev, experience: e.target.value }))}
                fullWidth
                required
                placeholder="e.g., 5 years in web development, worked with startups and enterprises"
              />
              
              <TextField
                label="Key Skills"
                value={generationForm.skills}
                onChange={(e) => setGenerationForm(prev => ({ ...prev, skills: e.target.value }))}
                fullWidth
                required
                multiline
                rows={2}
                placeholder="e.g., React, Node.js, UI/UX Design, Project Management"
              />
              
              <TextField
                label="Notable Achievements"
                value={generationForm.achievements}
                onChange={(e) => setGenerationForm(prev => ({ ...prev, achievements: e.target.value }))}
                fullWidth
                required
                multiline
                rows={2}
                placeholder="e.g., Led team of 5, Increased performance by 40%, Launched successful product"
              />
              
              <TextField
                label="Industry"
                value={generationForm.industry}
                onChange={(e) => setGenerationForm(prev => ({ ...prev, industry: e.target.value }))}
                fullWidth
                required
                placeholder="e.g., Technology, Healthcare, Finance"
              />
              
              <TextField
                label="Target Audience"
                value={generationForm.targetAudience}
                onChange={(e) => setGenerationForm(prev => ({ ...prev, targetAudience: e.target.value }))}
                fullWidth
                required
                placeholder="e.g., Tech startups, Enterprise clients, Job recruiters"
              />
              
              <FormControl fullWidth>
                <InputLabel>Writing Style</InputLabel>
                <Select
                  value={generationForm.style}
                  onChange={(e) => setGenerationForm(prev => ({ ...prev, style: e.target.value }))}
                  label="Writing Style"
                >
                  <MenuItem value="professional">Professional</MenuItem>
                  <MenuItem value="creative">Creative</MenuItem>
                  <MenuItem value="technical">Technical</MenuItem>
                  <MenuItem value="executive">Executive</MenuItem>
                </Select>
              </FormControl>
            </Stack>
          </DialogContent>
          
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button 
              onClick={() => setGenerateDialogOpen(false)}
              disabled={generatingAbout}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleGenerateAbout}
              disabled={generatingAbout || !generationForm.role || !generationForm.experience}
              startIcon={generatingAbout ? <CircularProgress size={20} /> : <AutoAwesomeIcon />}
              sx={{
                bgcolor: '#9c27b0',
                '&:hover': {
                  bgcolor: '#7b1fa2'
                }
              }}
            >
              {generatingAbout ? 'Generating...' : 'Generate About Section'}
            </Button>
          </DialogActions>
        </Dialog>

        {generatedAboutText && (
          <Card className="content-paper" sx={{ mt: 3, mb: 4, borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.05)', border: '1px solid rgba(0, 0, 0, 0.08)' }}>
            <Typography variant="subtitle1" className="section-title" sx={{ p: 2, bgcolor: 'rgba(156, 39, 176, 0.1)', fontWeight: 500, color: '#9c27b0' }}>
              ✨ AI Generated About Section Suggestion
            </Typography>
            <CardContent>
              <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>
                {generatedAboutText}
              </Typography>
              <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                <Button 
                  variant="outlined" 
                  size="small"
                  onClick={() => {
                    navigator.clipboard.writeText(generatedAboutText);
                    enqueueSnackbar('Copied to clipboard!', { variant: 'info' });
                  }}
                  sx={{ mr: 1 }}
                >
                  Copy Text
                </Button>
                <Button 
                  variant="contained" 
                  size="small"
                  onClick={() => {
                    setProfileData(prev => ({ ...prev, about: generatedAboutText }));
                    analyzeAboutSection(generatedAboutText);
                    setGeneratedAboutText(null);
                    enqueueSnackbar('About section updated and analysis initiated!', { variant: 'success' });
                  }}
                >
                  Use This Text & Analyze
                </Button>
              </Box>
            </CardContent>
          </Card>
        )}
      </Container>
    </>
  );
};

export default AboutSection; 